import { useEffect, useId, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { OfflineBadge } from "../offline/OfflineBadge";
import { useOfflineSync } from "../offline/useOffline";
import { can, hasModule, useMe } from "../lib/auth";
import { useBuildingContext } from "../lib/buildingContext";
import { useKeepClientAndBuildingInStep } from "../lib/clientContext";
import { useTheme } from "../lib/theme";
import { useMediaQuery } from "../lib/useMediaQuery";
import { BrandMark } from "./BrandMark";
import { BuildingSwitcher } from "./BuildingSwitcher";
import { ClientSwitcher } from "./ClientSwitcher";
import { IconCollapse, IconExpand, IconHelp, IconMenu, IconPlus, IconScan, IconSearch } from "./icons";
import { MenuSheet } from "./MoreSheet";
import { CommandMenu, commandShortcut, openCommandMenu, useCommandMenuShortcut } from "./CommandMenu";
import { OrgLogo } from "./OrgLogo";
import {
  buildingPath,
  HELP_PATH,
  navGroups,
  ORG_PATH,
  PROFILE_PATH,
  sectionOfPath,
  tabItems,
  type NavItem,
} from "./navItems";
import { SubscriptionBanner } from "./SubscriptionBanner";
import { StatusBanner } from "../features/status/StatusBanner";
import { NewDot } from "../features/helpcenter/NewDot";
import { ProfileMenu } from "./ProfileMenu";
import { RailTooltip } from "./RailTooltip";
import { useLogout } from "./useLogout";
import { useSwitchOrg } from "./useSwitchOrg";
import { useIntakeCount } from "../pages/Intake";
import { useDirectoryReplyCount } from "../features/directory/Replies";
import { useMentions } from "../features/logbook/Mentions";

const RAIL_KEY = "pw-rail-collapsed";
const readCollapsed = () => {
  try {
    return localStorage.getItem(RAIL_KEY) === "1";
  } catch {
    return false;
  }
};

/** Op een gebouwpagina opent "Bevinding" het formulier daar; elders de meldpagina. */
export const NEW_ISSUE_EVENT = "pw-new-issue";

export function Layout() {
  const { data: me } = useMe();
  const [theme, setTheme] = useTheme();
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const navigate = useNavigate();
  const logout = useLogout();
  const loc = useLocation();
  const { id: buildingId } = useBuildingContext();
  useOfflineSync();
  useOrgFromLink();
  useKeepClientAndBuildingInStep();

  // Pad waarop "Menu" geopend is: na navigeren (ook met de terug-knop) is de sheet vanzelf dicht.
  const [menuAt, setMenuAt] = useState<string | null>(null);
  const menuOpen = menuAt === loc.pathname;
  const menuId = useId();

  const canCreate = can(me, "issue.create");
  const canScan = hasModule(me, "assets");
  // Inbox: te beoordelen meldingen plus ongelezen antwoorden op mails uit het adresboek.
  const intakeOpen =
    (useIntakeCount(hasModule(me, "intake")).data ?? 0) +
    (useDirectoryReplyCount(hasModule(me, "intake") && hasModule(me, "directory")).data ?? 0);
  // Ongelezen @-vermeldingen tellen bij Mijn taken (tabblad Vermeldingen).
  const mentionsUnread = useMentions(hasModule(me, "issues")).data?.unread ?? 0;
  const groups = navGroups(me, buildingId).map((g) => ({
    ...g,
    items: g.items.map((i) =>
      i.key === "inbox"
        ? { ...i, count: intakeOpen }
        : i.key === "mijntaken" && mentionsUnread
          ? { ...i, count: mentionsUnread }
          : i,
    ),
  }));
  const tabs = tabItems(groups, buildingId);
  // Opdrachtmenu (Ctrl/Cmd+K of "/"): zoeken en snel naar een onderdeel of actie.
  useCommandMenuShortcut(Boolean(me?.org));
  const prefersDark = useMediaQuery("(prefers-color-scheme: dark)");
  const shownTheme = theme === "system" ? (prefersDark ? "dark" : "light") : theme;
  const isActive = (item: NavItem) => item.active(loc.pathname);
  const menuActive =
    !tabs.some(isActive) &&
    loc.pathname !== "/scan" &&
    loc.pathname !== "/melden" &&
    (groups.some((g) => g.items.some(isActive)) ||
      [PROFILE_PATH, HELP_PATH, ORG_PATH].some((p) => loc.pathname === p || loc.pathname.startsWith(`${p}/`)));
  // Hulp onthoudt vanaf welk scherm hij werd geopend (context bij een bericht aan support).
  const helpTo = loc.pathname.startsWith(HELP_PATH)
    ? HELP_PATH
    : `${HELP_PATH}?vanaf=${encodeURIComponent(loc.pathname + loc.search)}`;
  const section = sectionOfPath(loc.pathname);
  // De tekening en lijsten (BuildingPage) vangen "+ Bevinding" zelf op; Overzicht en Gebouw niet.
  const onBuilding = section !== null && section !== "overzicht" && section !== "gebouw";

  // Op de tekening en op smalle schermen is de rail ingeklapt; uitklappen gaat daar als
  // overlay over de pagina (de tekening wordt niet smaller) en wordt niet bewaard.
  const narrow = useMediaQuery("(max-width: 1100px)");
  const compact = section === "tekening" || narrow;
  const [overlayAt, setOverlayAt] = useState<string | null>(null);
  const overlay = compact && overlayAt === loc.pathname;
  const railCollapsed = compact ? !overlay : collapsed;
  const railRef = useRef<HTMLElement>(null);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const railId = useId();

  useEffect(() => {
    if (!overlay) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOverlayAt(null);
      toggleRef.current?.focus();
    };
    const onDown = (e: PointerEvent) => {
      if (!railRef.current?.contains(e.target as Node)) setOverlayAt(null);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown);
    };
  }, [overlay]);

  const closeOverlay = () => setOverlayAt(null);

  /** Op de gebouwpagina opent het formulier daar; in een gekozen gebouw eerst naar de tekening; anders de meldpagina. */
  function newIssue() {
    closeOverlay();
    if (onBuilding) window.dispatchEvent(new Event(NEW_ISSUE_EVENT));
    else if (buildingId) navigate(`${buildingPath(buildingId, "tekening")}?nieuw=1`);
    else navigate("/melden");
  }

  function toggleRail() {
    if (compact) {
      setOverlayAt(overlay ? null : loc.pathname);
      return;
    }
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem(RAIL_KEY, next ? "1" : "0");
    } catch {
      /* geen opslag */
    }
  }

  const reportActive = loc.pathname === "/melden";

  const railItem = (item: NavItem) => (
    <Link
      key={item.key}
      to={item.to}
      className="navitem"
      data-tip={item.label}
      title={item.title !== item.label ? item.title : undefined}
      aria-current={isActive(item) ? "page" : undefined}
      onClick={closeOverlay}
    >
      <item.Icon size={20} aria-hidden="true" />
      <span className="navlabel">{item.label}</span>
      {/* Teller is een extra: de linknaam blijft "Inbox". */}
      {item.count ? (
        <span className="navcount num" aria-hidden="true">
          {item.count}
        </span>
      ) : null}
    </Link>
  );
  const tab = (item: NavItem) => (
    <Link key={item.key} to={item.to} className="tab" aria-current={isActive(item) ? "page" : undefined}>
      <item.Icon size={24} aria-hidden="true" />
      {item.label}
    </Link>
  );

  return (
    <div className={`app ${railCollapsed ? "rail-collapsed" : ""} ${overlay ? "rail-overlay" : ""}`}>
      <nav className="rail" aria-label="Hoofdmenu" id={railId} ref={railRef}>
        <Link to="/" className="rail-brand" aria-label="Naar start" onClick={closeOverlay}>
          <OrgLogo
            org={me?.org}
            className="rail-org-logo"
            icon={railCollapsed}
            fallback={
              <>
                <BrandMark size={34} />
                <span className="navlabel wordmark">klantpunt</span>
              </>
            }
          />
        </Link>
        {groups.map((g, i) => (
          <div key={g.label ?? i} className="navsection" role={g.label ? "group" : undefined} aria-label={g.label}>
            {g.label && <p className="label navgroup navlabel">{g.label}</p>}
            {g.items.map(railItem)}
          </div>
        ))}
        <span className="grow" />
        <NavLink to={helpTo} className="navitem" data-tip="Hulp">
          <IconHelp size={20} aria-hidden="true" />
          <span className="navlabel">Hulp</span>
          <NewDot />
        </NavLink>
        <button
          type="button"
          ref={toggleRef}
          className="navitem railtoggle"
          onClick={toggleRail}
          aria-expanded={!railCollapsed}
          aria-controls={railId}
          data-tip="Menu uitklappen"
        >
          {railCollapsed ? <IconExpand size={20} aria-hidden="true" /> : <IconCollapse size={20} aria-hidden="true" />}
          <span className="navlabel">{railCollapsed ? "Menu uitklappen" : "Menu inklappen"}</span>
        </button>
        {railCollapsed && <RailTooltip railRef={railRef} />}
      </nav>

      <div className="appcol">
        <header className="topbar">
          <Link to="/" className="brandlink" aria-label="Naar start">
            <OrgLogo org={me?.org} className="topbar-org-logo" icon fallback={<BrandMark size={30} />} />
          </Link>
          {me?.org && <ClientSwitcher />}
          {me?.org && <BuildingSwitcher />}
          {me?.org && (
            <span className="org-name hide-mobile" title={me.org.name}>
              <span className="org-name-text">{me.org.name}</span>
              {me.orgs.length > 1 && (
                <Link to="/organisatie" className="muted small orgswitch">
                  wisselen
                </Link>
              )}
            </span>
          )}
          <span className="grow" />
          <OfflineBadge />
          {me?.org && (
            <button
              type="button"
              className="btn ghost cmdk-trigger"
              onClick={openCommandMenu}
              aria-label="Zoeken"
              aria-keyshortcuts="Control+K Meta+K /"
              title={`Zoeken (${commandShortcut()})`}
            >
              <IconSearch size={20} aria-hidden="true" />
              <span className="hide-mobile">Zoeken</span>
              <kbd className="hide-mobile" aria-hidden="true">
                {commandShortcut()}
              </kbd>
            </button>
          )}
          {canScan && (
            <Link
              to="/scan"
              className="btn hide-mobile"
              title="QR-code scannen"
              aria-current={loc.pathname === "/scan" ? "page" : undefined}
            >
              <IconScan size={20} aria-hidden="true" />
              <span className="tb-label">Scannen</span>
            </Link>
          )}
          {canCreate && (
            <button
              type="button"
              className="btn primary hide-mobile"
              onClick={newIssue}
              aria-current={reportActive ? "page" : undefined}
              aria-label={buildingId ? "Nieuwe bevinding in dit gebouw" : "Nieuwe bevinding"}
            >
              <IconPlus size={20} aria-hidden="true" />
              Bevinding
            </button>
          )}
          {me && <ProfileMenu me={me} theme={theme} setTheme={setTheme} onLogout={() => void logout()} />}
        </header>
        {me?.org && <SubscriptionBanner me={me} />}
        <StatusBanner />
        <main>
          <Outlet />
        </main>
        <nav className="tabbar" aria-label="Hoofdmenu">
          {tabs.map(tab)}
          {canCreate && (
            <button
              type="button"
              className="tab tab-main"
              onClick={newIssue}
              aria-current={reportActive ? "page" : undefined}
              aria-label={buildingId ? "Nieuwe bevinding in dit gebouw" : "Nieuwe bevinding"}
            >
              <span className="tab-plus">
                <IconPlus size={26} aria-hidden="true" />
              </span>
              Bevinding
            </button>
          )}
          {canScan && (
            <Link to="/scan" className="tab" aria-current={loc.pathname === "/scan" ? "page" : undefined}>
              <IconScan size={24} aria-hidden="true" />
              Scannen
            </Link>
          )}
          <button
            type="button"
            className="tab"
            onClick={() => setMenuAt(loc.pathname)}
            aria-expanded={menuOpen}
            aria-controls={menuOpen ? menuId : undefined}
            aria-haspopup="dialog"
            aria-current={menuActive ? "page" : undefined}
          >
            <IconMenu size={24} aria-hidden="true" />
            Menu
          </button>
        </nav>
        {me?.org && (
          <CommandMenu
            orgId={me.org.id}
            groups={groups}
            canCreate={canCreate}
            canScan={canScan}
            helpTo={helpTo}
            theme={shownTheme}
            onNewIssue={newIssue}
            onToggleTheme={() => setTheme(shownTheme === "dark" ? "light" : "dark")}
          />
        )}
        {menuOpen && (
          <MenuSheet
            id={menuId}
            groups={groups}
            pathname={loc.pathname}
            helpTo={helpTo}
            showOrg={can(me, "users.manage")}
            onClose={() => setMenuAt(null)}
          />
        )}
      </div>
    </div>
  );
}

/**
 * Link uit een pushmelding (`?org=<id>`): open in die organisatie. Wissel als je daar lid van bent, en haal de
 * parameter daarna uit het adres.
 */
function useOrgFromLink() {
  const { data: me } = useMe();
  const loc = useLocation();
  const navigate = useNavigate();
  const switchOrg = useSwitchOrg();
  const wanted = new URLSearchParams(loc.search).get("org");
  useEffect(() => {
    if (!wanted || !me) return;
    const params = new URLSearchParams(loc.search);
    params.delete("org");
    const rest = params.toString();
    const to = `${loc.pathname}${rest ? `?${rest}` : ""}${loc.hash}`;
    if (me.org?.id !== wanted && me.orgs.some((o) => o.id === wanted)) void switchOrg(wanted, to);
    else navigate(to, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wanted, me?.org?.id]);
}
